<template>
  <div class="step-container">
    <span class="step-title">Set pre-release services</span>
    <span class="step-subtitle">
      Select the services that you want to appear on your pre-release page.
    </span>
    <div class="step-content">
      <div class="step-layout">
        <div class="destinations-container">
          <div class="destinations">
            <draggable v-model="edited_destinations" handle=".handle-icon">
              <div
                v-for="(destination, index) in edited_destinations"
                :key="destination.id"
              >
                <template
                  v-if="destination.type === 'text' && !sms_activated"
                ></template>
                <template
                  v-else-if="destination.type === 'custom' && !destination.url"
                ></template>
                <div class="destination-container" v-else>
                  <div class="handle-icon">
                    <DraghandleIcon />
                  </div>
                  <div class="destination">
                    <div class="top">
                      <div class="destination-logo">
                        <img
                          :src="
                            destination.logo ||
                              '/img/destinations/' + destination.type + '.png'
                          "
                          :alt="destination.name"
                        />
                      </div>
                      <div
                        class="platform"
                        v-if="destination.type !== 'custom'"
                      >
                        {{ destination.description }}
                      </div>
                      <div class="platform" v-else>
                        {{ destination.cta }}
                      </div>
                      <div
                        class="cta-switch"
                        v-if="destination.type !== 'custom'"
                      >
                        <label class="switch">
                          <input
                            :checked="destination.enabled"
                            @change="
                              event => updateDestinationEnabled(event, index)
                            "
                            type="checkbox"
                            class="switch-input"
                          />
                          <span
                            class="switch-background"
                            data-on="On"
                            data-off="Off"
                          ></span>
                          <span class="switch-handle"></span>
                        </label>
                      </div>
                      <div
                        class="destination-action"
                        @click="removeDestination(index)"
                        v-else
                      >
                        <RemoveIcon />
                      </div>
                      <div
                        class="dropdown-icon"
                        v-if="
                          !(destination.url || '').includes('/placeholder/')
                        "
                        :class="{ inverted: destination.expand }"
                        @click="destination.expand = !destination.expand"
                      >
                        <ChevronDownIcon />
                      </div>
                    </div>
                    <template
                      v-if="
                        destination.expand &&
                          !(destination.url || '').includes('/placeholder/')
                      "
                    >
                      <div class="details">
                        <div
                          class="url-field"
                          v-if="destination.type !== 'text'"
                        >
                          <label class="default-input">
                            <span class="label"
                              >{{ destination.name }} URL</span
                            >
                            <input
                              v-model="destination.url"
                              type="text"
                              :placeholder="destination | placeholderFilter"
                            />
                          </label>
                        </div>
                        <div
                          class="cta-field"
                          v-if="destination.type === 'custom'"
                        >
                          <label class="default-input">
                            <span class="label">CTA</span>
                            <input
                              v-model="destination.cta"
                              :placeholder="destination.default_cta"
                              type="text"
                            />
                          </label>
                        </div>
                      </div>
                      <div
                        class="config message-body"
                        v-if="destination.config"
                      >
                        <label class="default-input">
                          <span class="label">Message Body</span>
                          <textarea
                            v-model="destination.config.message_body"
                            placeholder="Message Body"
                          ></textarea>
                          <div class="link">
                            {{ `https://${source.domain}/${source.path}` }}
                          </div>
                        </label>
                      </div>
                      <div
                        class="redirect-url-field"
                        v-if="'redirect_url' in destination"
                      >
                        <label class="default-input">
                          <span class="label">Redirect URL (Optional)</span>
                          <input
                            v-model="destination.redirect_url"
                            type="text"
                            placeholder="https://example.com"
                          />
                        </label>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </draggable>
          </div>
          <div class="add-sources">
            <div class="add-link-content">
              <div class="modal-content">
                <div class="des-background">
                  <div class="add-link">
                    <div class="title add-link-title">Add a Link</div>
                    <div class="url-field">
                      <span class="label enter-url">Enter URL</span>
                      <label class="default-input cus-add-link">
                        <input type="text" placeholder="URL" v-model="url" />
                      </label>
                      <button
                        class="button-default big"
                        @click="addLink(url)"
                        :disabled="fetching"
                      >
                        <span v-if="!fetching">Add</span>
                        <span v-else>Fetching...</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="landing-page-preview">
          <div class="layouts">
            <div @click="$refs.select.click()" class="select">
              <label>
                <span class="label">Theme</span>
              </label>
              <select
                ref="select"
                v-model="layoutTheme"
                @change="layoutThemeUpdated"
                class="standard-select"
              >
                <option value="2-dark">Flat - Dark</option>
                <option value="2-light">Flat - Light</option>
              </select>
              <ChevronDownIcon class="pointer-events-none" />
              <span class="focus"></span>
            </div>
          </div>
          <div v-if="source" class="landing-page-preview-container">
            <LandingPagePreview
              :link="source"
              :destinations="edited_destinations"
              v-if="source.layout === 1"
            />
            <LandingPagePreviewTwo
              :link="{
                ...source,
                tracks: [
                  {
                    url:
                      source.preview_url ||
                      source.youtube_preview_url ||
                      source.custom_preview_url
                  }
                ]
              }"
              :destinations="edited_destinations"
              :preRelease="true"
              v-if="source.layout === 2"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="step-actions">
      <div @click="back()" class="back">
        <ArrowLeftIcon />
        <span class="back-text">BACK</span>
      </div>
      <button
        @click="next()"
        :disabled="isLoading || !stepValidated"
        class="button-default big"
      >
        <span>Next</span>
      </button>
    </div>
    <SnackBar>
      <template v-slot:icon></template>
    </SnackBar>
  </div>
</template>
<script>
import draggable from "vuedraggable";
import LandingPagePreview from "@/components/Custom/LandingPagePreview";
import LandingPagePreviewTwo from "@/components/Custom/LandingPagePreviewTwo";
import SnackBar from "@/components/Custom/SnackBar";
import ArrowLeftIcon from "../../../public/img/icons/arrow-left.svg";
import ChevronDownIcon from "../../../public/img/icons/chevron-down.svg";
import DraghandleIcon from "../../../public/img/icons/drag-2.svg";
import RemoveIcon from "../../../public/img/icons/remove.svg";

import { Q_META } from "@/graphql";

export default {
  name: "LinkWizardPreReleaseServices",
  components: {
    ArrowLeftIcon,
    LandingPagePreview,
    LandingPagePreviewTwo,
    SnackBar,
    ChevronDownIcon,
    DraghandleIcon,
    RemoveIcon,
    draggable
  },
  props: ["action", "destinations"],
  data() {
    return {
      isLoading: false,
      source: null,
      edited_destinations: [],
      layoutTheme: "",
      destination_defaults: {
        spotify: {
          name: "Spotify",
          type: "spotify",
          default_cta: "Pre-Save",
          description: "Spotify Pre-Save",
          expand: false,
          pre_release: true,
          redirect_url: null
        },
        applemusic: {
          name: "Apple Music",
          type: "applemusic",
          default_cta: "Pre-Add",
          description: "Apple Music Pre-Add",
          expand: false,
          pre_release: true,
          redirect_url: null
        },
        text: {
          name: "Notify via Text",
          type: "text",
          default_cta: "Notify me via Text",
          description: "Notify via Text",
          expand: false,
          pre_release: true,
          config: {}
        },
        itunes: {
          name: "iTunes",
          type: "itunes",
          default_cta: "Pre-Order",
          description: "iTunes Pre-Order",
          expand: false,
          pre_release: true,
          redirect_url: null
        },
        beatport: {
          name: "Beatport",
          type: "beatport",
          default_cta: "Pre-Order",
          description: "Beatport Pre-Order",
          logo:
            "https://artisthub.b-cdn.net/site-icons/dynamic/www.beatport.com.webp",
          expand: false,
          pre_release: true
        },
        custom: {
          name: "Custom",
          type: "custom",
          default_cta: "Pre-Order",
          description: "Custom",
          expand: false,
          pre_release: true
        }
      },
      url: "",
      fetching: false
    };
  },
  filters: {
    placeholderFilter({ type }) {
      if (["spotify", "applemusic", "itunes"].includes(type))
        return "Please enter (Can be empty)";
      else return "Please enter";
    }
  },
  created() {
    if (
      this.action === "edit" &&
      !this.$store.state.editLinkWizardData.destinations.length
    ) {
      const preReleaseDestinations = this.destinations.length
        ? this.destinations.flatMap(el => {
            console.log({ el });
            if (!el.pre_release) return [];
            return [
              {
                ...el,
                description: this.destination_defaults[el.type].description,
                logo: el.logo || this.destination_defaults[el.type].logo,
                enabled: true,
                expand: el.type === "text"
              }
            ];
          })
        : [];

      this.$store.commit("setEditLinkWizardData", {
        preReleaseDestinations: preReleaseDestinations
      });
    }

    this.source =
      this.action === "edit"
        ? { ...this.$store.state.editLinkWizardData }
        : { ...this.$store.state.createLinkWizardData };

    this.source.layout = 2;

    this.layoutTheme = this.source.layout + "-" + this.source.theme;

    if (this.source.preReleaseDestinations.length) {
      this.edited_destinations = [...this.source.preReleaseDestinations];
      for (const [key, destination] of Object.entries(
        this.destination_defaults
      )) {
        if (key === "custom") continue;
        if (
          !this.source.preReleaseDestinations.some(
            el => el.type === key && el.pre_release
          )
        ) {
          this.edited_destinations.push({
            ...destination,
            url: null,
            cta: destination.default_cta,
            text: null,
            enabled: false,
            pre_release: true,
            new: true
          });
        }
      }
    } else {
      for (const [key, destination] of Object.entries(
        this.destination_defaults
      )) {
        // find source destination (Spotify)
        if (key === this.source.source_destination.type) {
          this.edited_destinations.push({
            ...destination,
            url: this.source.source_destination.url,
            cta: destination.default_cta,
            text: null,
            enabled: true,
            pre_release: true
          });
        } else if (key === "text") {
          this.edited_destinations.push({
            ...destination,
            url: null,
            cta: destination.default_cta,
            text: null,
            enabled: false,
            pre_release: true,
            config: {
              message_body: `${this.source.releaseTitle} is out now!`
            }
          });
        } else if (key !== "custom") {
          this.edited_destinations.push({
            ...destination,
            url: null,
            cta: destination.default_cta,
            text: null,
            enabled: false,
            pre_release: true
          });
        }
      }
    }
  },
  computed: {
    artist() {
      return this.$store.getters.getArtist;
    },
    user() {
      return this.$store.getters.getUser;
    },
    sms_activated() {
      return this.$store.getters.getActivatedFeatures.includes("sms");
    },
    commitName() {
      return this.action == "edit"
        ? "setEditLinkWizardData"
        : "setCreateLinkWizardData";
    },
    stepValidated() {
      return this.edited_destinations.every(el => {
        if (!el.enabled) return true;
        if (!el.cta) return false;
        if (el.type === "text") return !!el.config.message_body;
        if (el.type === "beatport") return !!el.url;
        if (el.url && !this.destinationUrlValid(el.type, el.url)) return false;
        if ("redirect_url" in el && !this.isValidUrl(el.redirect_url))
          return false;
        // if (this.source.rid) return true;
        return true;
      });
    },
    createLinkWizardData() {
      return this.$store.getters.getCreateLinkWizardData;
    }
  },
  methods: {
    isValidUrl(urlString) {
      if (!urlString) return true;
      try {
        const url = new URL(urlString);
        return url.protocol === "http:" || url.protocol === "https:";
      } catch (e) {
        return false;
      }
    },
    destinationUrlValid(type, url) {
      if (!url || url.trim() === "") return true;
      try {
        new URL(url);
        return true;
      } catch (e) {
        return false;
      }
    },
    back() {
      this.$store.commit(this.commitName, {
        ...this.source,
        preReleaseDestinations: this.edited_destinations.map(el => ({
          ...el,
          url: el.type === "text" && el.enabled ? "#" : el.url
        }))
      });
      this.$emit("back");
    },
    next() {
      this.$store.commit(this.commitName, {
        ...this.source,
        preReleaseDestinations: this.edited_destinations.map(el => ({
          ...el,
          url: el.type === "text" && el.enabled ? "#" : el.url
        }))
      });
      this.$emit("setStep", this.action == "edit" ? 4 : 5);
    },
    updateDestinationEnabled(event, index) {
      console.log(event);
      this.edited_destinations[index].enabled = event.target.checked;
      this.edited_destinations[index].expand = event.target.checked;
    },
    layoutThemeUpdated(e) {
      const [layout, theme] = e.target.value.split("-");
      this.source.layout = Number(layout);
      this.source.theme = theme;
    },
    async addLink(url) {
      this.fetching = true;
      const r = await this.$apollo.query({
        query: Q_META,
        client: "localClient",
        variables: { url },
        fetchPolicy: "network-only"
      });

      const metaData = JSON.parse(r.data.meta.data);

      console.log("metaData", metaData);

      const destination = {
        ...this.destination_defaults.custom,
        enabled: true,
        new: true,
        expand: true,
        url,
        text: metaData.og_title || "Custom",
        description: metaData.og_title || "Custom",
        logo: metaData.domain_image || null,
        cta: metaData.og_title || "Pre-Order",
        deeplink_android: metaData.deeplinks?.android || null,
        deeplink_ios: metaData.deeplinks?.ios || null
      };

      this.edited_destinations.push(destination);
      this.url = "";
      this.fetching = false;
    },
    removeDestination(index) {
      this.edited_destinations[index].enabled = false;
      this.edited_destinations[index].url = null;
    }
  }
};
</script>
<style lang="scss" scoped>
.step-container {
  padding-top: 60px;

  .step-title {
    display: block;
    text-align: center;
    font-weight: bold;
    font-size: 30px;
    line-height: 42px;
    /* White */
    color: white;
    mix-blend-mode: normal;
    opacity: 0.9;
  }

  .step-subtitle {
    display: block;
    text-align: center;
    margin-top: 6px;
    /* 16/Regular */
    font-size: 16px;
    line-height: 24px;
    /* Text/type2 */
    color: $text-type-2;
  }

  .step-content {
    margin-top: 30px;

    //width: 620px;
    .description {
      display: block;
      width: 100%;
      text-align: center;
      /* 16/Regular */
      font-size: 16px;
      line-height: 24px;
      /* Text/type0 */
      color: $text-type-0;
    }

    .step-layout {
      display: grid;
      grid-template-columns: auto 340px;
      grid-column-gap: 40px;
      margin-top: 36px;

      .destinations-container {
        .destinations {
          .destination-container {
            display: flex;
            align-items: center;
            margin-bottom: 24px;

            .handle-icon {
              width: 20px;
              margin-right: 10px;

              svg {
                width: 18px;
                path {
                  fill: white;
                }
              }

              &:hover {
                cursor: grab;

                svg {
                  path {
                    fill: $purple;
                  }
                }
              }
            }

            .destination {
              width: 100%;
              position: relative;
              padding: 16px 24px;
              background-color: #24224a;
              border-radius: 10px;

              .top {
                max-width: 600px;
                display: flex;
                align-items: center;

                .destination-logo {
                  display: flex;
                  justify-content: center;

                  img {
                    max-width: 135px;
                    max-height: 51px;
                    border-radius: 10px;
                  }
                }

                .platform {
                  margin-left: 18px;
                  font-weight: 600;
                  font-size: 16px;
                  line-height: 24px;
                  color: #ffffff;
                }

                .cta-switch {
                  margin-left: auto;
                }

                .destination-action {
                  border-radius: 5px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  margin-left: auto;

                  &:hover {
                    cursor: pointer;

                    svg {
                      path {
                        fill: white;
                      }
                    }
                  }

                  svg {
                    width: 24px;

                    path {
                      fill: $text-type-0;
                    }
                  }

                  &.active {
                    background: #9c27b0;
                    border-color: #ffffff;

                    svg {
                      path {
                        fill: white;
                      }
                    }
                  }
                }

                .dropdown-icon {
                  margin-left: 23px;
                  display: flex;
                  align-items: center;
                  cursor: pointer;

                  svg {
                    width: 20px;

                    path {
                      stroke: white;
                    }
                  }
                }

                .inverted {
                  transform: rotate(180deg);
                }
              }

              .details {
                margin-top: 30px;
                display: flex;

                .url-field {
                  flex: 1;

                  input {
                    padding: 8px 10px;
                    line-height: 24px;
                  }
                }

                .cta-field {
                  input {
                    padding: 8px 10px;
                    line-height: 24px;
                  }
                }
              }

              .config {
                margin-top: 15px;

                textarea {
                  resize: none;
                  border-bottom: 0;
                  border-bottom-left-radius: 0;
                  border-bottom-right-radius: 0;
                }

                .link {
                  width: 100%;
                  font-size: 14px;
                  line-height: 14px;
                  color: #8786ab;
                  background: #100e2a;
                  border: 1px solid #312f62;
                  border-top: 0;
                  border-bottom-left-radius: 5px;
                  border-bottom-right-radius: 5px;
                  padding: 0 11px 13px;
                }
              }

              .redirect-url-field {
                margin-top: 15px;
                input {
                  padding: 8px 10px;
                  line-height: 24px;
                }
              }
            }
          }
        }

        .add-sources {
          .add-link-content {
            .modal-content {
              .des-background {
                width: calc(100% - 28px);
                position: relative;
                padding: 24px;
                padding-bottom: 14px;
                background-color: #24224a;
                border-radius: 10px;
                margin: 0px auto;
                margin-left: 28px;

                .add-link {
                  .add-link-title {
                    margin-bottom: 30px;
                    font-weight: 500;
                    font-size: 22px;
                  }

                  .url-field {
                    .enter-url {
                      font-weight: 600;
                    }

                    .cus-add-link {
                      display: flex;
                      justify-content: start;
                      align-items: center;
                      flex-direction: row;
                      margin-top: 15px;
                      margin-bottom: 10px;
                      gap: 15px;
                    }

                    button {
                      margin-bottom: 10px;
                    }
                  }
                }
              }
            }
          }
        }
      }

      .landing-page-preview *::-webkit-scrollbar {
        display: none;
      }

      .landing-page-preview {
        width: 100%;

        .layouts {
          display: flex;
          /* border: 1px solid #9c27b0; */
          border-radius: 5px;
          margin-bottom: 20px;

          .layout {
            width: 50%;
            font-size: 0.9rem;
            font-weight: 900;
            color: #8786ab;
            padding: 15px;
            text-align: center;
            cursor: pointer;
          }

          .selected {
            background-color: #9c27b0;
            color: white;
          }

          .disabled {
            cursor: no-drop;
          }
        }

        .landing-page-preview-container {
          border-radius: 5px;
          overflow: hidden;
        }
      }
    }
  }

  .step-actions {
    margin-top: auto;
    padding-top: 50px;
    padding-bottom: 60px;
    display: flex;
    justify-content: space-between;

    .back {
      cursor: pointer;
      display: flex;
      //flex-direction: column;
      justify-content: flex-start;
      align-items: center;

      &:hover {
        svg {
          path {
            fill: white;
          }
        }

        .back-text {
          color: white;
        }
      }

      svg {
        width: 28px;

        path {
          fill: #8786ab;
        }
      }

      .back-text {
        font-weight: 600;
        font-size: 14px;
        line-height: 14px;
        text-transform: uppercase;
        /* Text/type2 */
        color: $text-type-2;
      }
    }

    .button-default {
      padding: 9px 40px;
    }
  }
}

@media (max-width: 1140px) {
  .step-layout {
    grid-column-gap: 20px !important;
  }
  .destination-container {
    .destination {
      .top {
        width: 400px !important;
      }
    }
  }
}

@media (max-width: 900px) {
  .destination-container {
    .destination {
      padding: 16px 10px !important;
      .top {
        width: 335px !important;
      }
    }
  }
}

@media (max-width: 800px) {
  .step-container {
    .step-content {
      .step-layout {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
      }
    }
  }
  .destinations-container {
    width: 100% !important;
    .destination-container {
      .destination {
        .top {
          width: 100% !important;
        }
      }
    }
  }
  .landing-page-preview {
    width: 340px !important;
  }
}

@media (max-width: 600px) {
  .step-container {
    width: 80%;
  }

  .landing-page-preview {
    display: none;
  }
  .step-title {
    margin-top: 30px;
  }

  .destination-logo img {
    height: 30px;
    width: 30px;
  }
  .platform {
    font-size: 12px !important;
    line-height: 1.2 !important;
  }
}
</style>
